import { endpointFilterType } from '../../../infrastructure/constants/report/endpointFilterType';
import { htmlIds } from '../../../infrastructure/helpers/report/navigation/reportConfigNavigation.HtmlIds';
import ObjectIndexer from '../../../infrastructure/types/ObjectIndexer';
import Assignment from '../../../types/functions/Assignment/Assignment';
import locationType from '../../../types/functions/Location/LocationType';
import ReportLogicLocation from '../../../types/functions/Location/ReportLogicLocation';
import Aggregation from '../../../types/report/Aggregation';
import ComponentNumberAndAssignment from '../../../types/report/ComponentNumberAndAssignment';
import DataSourceField from '../../../types/report/DataSourceField';
import Dictionary from '../../../types/report/Dictionary';
import DictionaryField from '../../../types/report/DictionaryField';
import ReportCase from '../../../types/report/ReportCase';
import ReportCaseField from '../../../types/report/ReportCaseField';
import ReportConfig from '../../../types/report/ReportConfig';
import Selector from '../../../types/report/Selector';

const getDefaultExpanded = (): ObjectIndexer<boolean> => {

    let result = {} as ObjectIndexer<boolean>;

    result[htmlIds.dataSources] = Boolean(localStorage.getItem(htmlIds.dataSources));
    result[htmlIds.cacheKeys] = Boolean(localStorage.getItem(htmlIds.cacheKeys));
    result[htmlIds.lookups] = Boolean(localStorage.getItem(htmlIds.lookups));
    result[htmlIds.variables] = Boolean(localStorage.getItem(htmlIds.variables));
    result[htmlIds.aggregations] = Boolean(localStorage.getItem(htmlIds.aggregations));
    result[htmlIds.filters] = Boolean(localStorage.getItem(htmlIds.filters));
    result[htmlIds.endpointFilters] = Boolean(localStorage.getItem(htmlIds.endpointFilters));
    result[htmlIds.expectedUnderReport] = Boolean(localStorage.getItem(htmlIds.expectedUnderReport));
    result[htmlIds.expectedOverReport] = Boolean(localStorage.getItem(htmlIds.expectedOverReport));
    result[htmlIds.cases] = Boolean(localStorage.getItem(htmlIds.cases));
    result[htmlIds.matchingKeys] = Boolean(localStorage.getItem(htmlIds.matchingKeys));
    result[htmlIds.enrichment] = Boolean(localStorage.getItem(htmlIds.enrichment));
    result[htmlIds.validations] = Boolean(localStorage.getItem(htmlIds.validations));
    result[htmlIds.import] = Boolean(localStorage.getItem(htmlIds.import));

    return result;
};

const removeExpandedFromLocalStorage = (): void => {

    localStorage.removeItem(htmlIds.dataSources)
    localStorage.removeItem(htmlIds.cacheKeys)
    localStorage.removeItem(htmlIds.lookups)
    localStorage.removeItem(htmlIds.variables)
    localStorage.removeItem(htmlIds.filters)
    localStorage.removeItem(htmlIds.endpointFilters)
    localStorage.removeItem(htmlIds.expectedUnderReport)
    localStorage.removeItem(htmlIds.expectedOverReport)
    localStorage.removeItem(htmlIds.cases)
    localStorage.removeItem(htmlIds.matchingKeys)
    localStorage.removeItem(htmlIds.enrichment)
    localStorage.removeItem(htmlIds.validations)
    localStorage.removeItem(htmlIds.import)
};

const keyword = {
    if: 'IF',
    lookup: 'LOOKUP',
    exclude: 'THEN EXCLUDE RECORD',
    report: 'THEN REPORT RECORD',
    underReport: 'THEN RECORD IS EXPECTED UNDER REPORT',
    overReport: 'THEN RECORD IS EXPECTED OVER REPORT'
};

const findAssignment = (location: ReportLogicLocation, reportConfig: ReportConfig): Assignment => {

    switch (location.statement) {
        case locationType.statement.lookup: return getAssignment(reportConfig.lookups, location.statementNumber);
        case locationType.statement.validation: return getAssignment(reportConfig.validations, location.statementNumber);
        case locationType.statement.variable: return getAssignment(reportConfig.variables, location.statementNumber);
        case locationType.statement.fcaRegulatedEntitiesSelector: return getAssignment(reportConfig.fcaRegulatedEntitiesSelectors, location.statementNumber);
        case locationType.statement.filter: return getAssignment(reportConfig.filters, location.statementNumber);
        case locationType.statement.underReportFilter: return getAssignment(reportConfig.endpointFilters.filter(x=>x.type===endpointFilterType.underReport), location.statementNumber)
        case locationType.statement.overReportFilter: return getAssignment(reportConfig.endpointFilters.filter(x => x.type === endpointFilterType.overReport), location.statementNumber)
        case locationType.statement.endpointFilter: return getAssignment(reportConfig.endpointFilters.filter(x => x.type === endpointFilterType.regular), location.statementNumber);
        case locationType.statement.firdsEsmaInstrumentSelector: return getSelectorAssignmentByKey(reportConfig.firdsEsmaInstrumentSelectors, location.statementNumber, location.selectorKey ||'');
        case locationType.statement.firdsFcaInstrumentSelector: return getSelectorAssignmentByKey(reportConfig.firdsFcaInstrumentSelectors, location.statementNumber, location.selectorKey || '');
        case locationType.statement.aggregation: return getAssignment(reportConfig.aggregations, location.statementNumber);
        case locationType.statement.aggregatedRecordField: return getAggregationFieldAssignment(reportConfig.aggregations, location.statementNumber, location.fieldId);
        case locationType.statement.dictionaryField: return getDictionaryFieldAssignment(reportConfig.dictionaries, location.statementNumber, location.fieldId);
        case locationType.statement.userDefinedFunction: return getAssignment(reportConfig.userDefinedFunctions, location.statementNumber);
        case locationType.statement.annaDsbUpiEnrichmentSelector: return getSelectorAssignmentByKey(reportConfig.annaDsbUpiEnrichmentSelectors, location.statementNumber, location.selectorKey || '');
        case locationType.statement.annaDsbUpiSelector: return getAssignment(reportConfig.annaDsbUpiSelectors, location.statementNumber);
        case locationType.statement.case: return getAssignment(reportConfig.cases, location.statementNumber);
        case locationType.statement.exchangeRatesSelector: return getAssignment(reportConfig.exchangeRatesSelectors, location.statementNumber);
        case locationType.statement.matchingKey: return reportConfig.matchingKey.assignment;
        case locationType.statement.matchingKeyEndPoint: return reportConfig.matchingKeyEndPoint.assignment;
        case locationType.statement.lseSelector: return getAssignment(reportConfig.lseSelectors, location.statementNumber);
        case locationType.statement.leiSelector: return getAssignment(reportConfig.leiSelectors, location.statementNumber);
        case locationType.statement.isinSelector: return getAssignment(reportConfig.isinSelectors, location.statementNumber);
        case locationType.statement.reportField: return setReportCaseFieldAssignment(reportConfig.cases, location.statementNumber, location.fieldId);

        default: return {} as Assignment;
    }
}

const setReportCaseFieldAssignment = (reportCases: ReportCase[], caseNumber: number, fieldId: number): Assignment => {
    let fields = reportCases.find(x => x.number === caseNumber)?.fields as ReportCaseField[];

    let field = fields.find(x => x.fieldId === fieldId) as ReportCaseField;

    return field.assignment;
}

const getAggregationFieldAssignment = (aggregations: Aggregation[], number: number, fieldId: number): Assignment => {

    let aggr = aggregations.find(x => x.number === number);

    let field = aggr?.fields.find(x => x.fieldId === fieldId) as DataSourceField;

    return field.assignment;
}

const getDictionaryFieldAssignment = (dictionaries: Dictionary[], number: number, fieldNumber: number): Assignment => {

    let dict = dictionaries.find(x => x.number === number);

    let field = dict?.fields.find(x => x.number === fieldNumber) as DictionaryField;

    return field.assignment;
}

const getSelectorAssignmentByKey = (selectors: Selector[], number: number,key:string): Assignment => {

    let item = selectors.find(x => x.number === number);

    return item?.keys?.find(x=>x.name===key)?.assignment as Assignment;
}

const getAssignment = (collection: ComponentNumberAndAssignment[], number: number): Assignment => {

    let item = collection.find(x => x.number === number);

    return item?.assignment ? item.assignment : { condition: item?.condition } as Assignment;
}

export { getDefaultExpanded, keyword, removeExpandedFromLocalStorage, findAssignment };
